/**
 * @module lib/api-client
 * @description Configured Axios client with in-memory authentication, request
 * correlation IDs, and normalized API errors.
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_BASE_URL } from "@/config/env";

export interface ApiSuccessEnvelope<T> {
  httpStatus?: number;
  message?: string;
  status: "success" | "failed";
  data: T;
  request_id?: string;
  error_code?: string;
  error_message?: string;
  error_details?: string;
}

interface ApiErrorPayload {
  message?: string;
  error_message?: string;
  error_details?: string;
  error_code?: string;
  request_id?: string;
}

/**
 * Structured error exposed to feature components without leaking raw Axios
 * internals into the UI.
 */
export class AppError extends Error {
  public readonly status?: number;
  public readonly errorCode?: string;
  public readonly errorDetails: string;
  public readonly requestId: string;
  public readonly isRetryable: boolean;

  constructor(
    params:
      | {
          message: string;
          status?: number;
          errorCode?: string;
          errorDetails?: string;
          requestId?: string;
          isRetryable?: boolean;
          cause?: unknown;
        }
      | {
          error_code?: string;
          error_message?: string;
          error_details?: string;
          request_id?: string;
          httpStatus?: number;
        },
    legacyStatus?: number
  ) {
    const normalized = "message" in params
      ? params
      : {
          message: params.error_message || "Request failed.",
          status: legacyStatus ?? params.httpStatus,
          errorCode: params.error_code,
          errorDetails: params.error_details,
          requestId: params.request_id,
        };
    super(normalized.message);
    this.name = "AppError";
    this.status = normalized.status;
    this.errorCode = normalized.errorCode;
    this.errorDetails = normalized.errorDetails || normalized.message;
    this.requestId = normalized.requestId || "";
    this.isRetryable =
      ("isRetryable" in normalized && normalized.isRetryable !== undefined)
        ? normalized.isRetryable
        : (normalized.status === undefined || normalized.status >= 500 || normalized.status === 408);
    if ("cause" in normalized && normalized.cause !== undefined) {
      Object.defineProperty(this, "cause", { value: normalized.cause, enumerable: false });
    }
  }

  /** Normalize an unknown failure at an I/O boundary. */
  public static fromUnknown(error: unknown, fallbackMessage = "An unexpected error occurred"): AppError {
    if (error instanceof AppError) return error;
    if (axios.isAxiosError<ApiErrorPayload>(error)) {
      return AppError.fromAxiosError(error);
    }
    if (error instanceof Error) {
      return new AppError({ message: error.message || fallbackMessage, cause: error });
    }
    return new AppError({ message: fallbackMessage, cause: error });
  }

  public static fromAxiosError(error: AxiosError<ApiErrorPayload>): AppError {
    const payload = error.response?.data;
    const status = error.response?.status;
    const message = payload?.error_message || payload?.message || error.message || "Request failed.";
    return new AppError({
      message,
      status,
      errorCode: payload?.error_code,
      errorDetails: payload?.error_details || message,
      requestId: payload?.request_id || responseRequestId(error.response),
      isRetryable: status === undefined || status >= 500 || status === 408 || status === 429,
      cause: error,
    });
  }
}

function responseRequestId(response?: AxiosResponse<ApiErrorPayload>): string | undefined {
  const value = response?.headers?.["x-request-id"];
  return typeof value === "string" ? value : undefined;
}

function createRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall back for older browsers or restricted execution contexts.
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

let accessToken: string | null = null;
let authFailureHandler: (() => void) | null = null;
let refreshPromise: Promise<string | null> | null = null;

/** Set or clear the short-lived access token kept only in memory. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** Read the in-memory access token (needed by fetch-based SSE, which cannot use axios). */
export function getAccessToken(): string | null {
  return accessToken;
}

/** Register the callback used when a refresh attempt cannot restore a session. */
export function setOnAuthFailure(handler: (() => void) | null): void {
  authFailureHandler = handler;
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers.set("X-Request-Id", createRequestId());
  if (accessToken) config.headers.set("Authorization", `Bearer ${accessToken}`);
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorPayload>) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const isRefreshRequest = original?.url?.includes("/auth/refresh/");

    if (status === 401 && original && !original._retry && !isRefreshRequest) {
      original._retry = true;
      try {
        const isClientPortal =
          (typeof window !== "undefined" &&
            window.location.pathname.startsWith("/client")) ||
          (typeof localStorage !== "undefined" &&
            localStorage.getItem("rx_auth_portal") === "client");
        const refreshEndpoint = isClientPortal
          ? "/client/auth/refresh/"
          : "/super-admin/auth/refresh/";

        refreshPromise ??= apiClient
          .post<ApiSuccessEnvelope<{ access_token: string }>>(refreshEndpoint)
          .then((response) => {
            const token = response.data.data.access_token;
            setAccessToken(token);
            return token;
          })
          .catch(() => null)
          .finally(() => {
            refreshPromise = null;
          });
        const token = await refreshPromise;
        if (token) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${token}`;
          return apiClient(original);
        }
      } catch {
        // A failed refresh is handled uniformly below so protected screens can log out.
      }
      setAccessToken(null);
      authFailureHandler?.();
    }

    throw AppError.fromAxiosError(error);
  }
);

export { apiClient };
